/**
 * GameChanger innings notation.
 *
 * GC writes innings the way baseball has always written them: as OUTS, not as
 * decimals. "2.1" is two-and-one-third innings, "2.2" is two-and-two-thirds, and
 * there is never a ".3" through ".9". Verified against a real 36-game export
 * (KC Cannon Ballers, Summer 2026) two ways:
 *
 *   1. Every fractional digit across all 143 fielding and IP values was 0, 1 or 2.
 *   2. The fielding "P" column was byte-identical to the pitching "IP" column for
 *      every player, and IP is unambiguously thirds.
 *
 * Reading those with parseFloat treats "2.1" as 2.1 instead of 2.333, and
 * Math.round then discards the remainder entirely. Worse, a short defensive
 * stint of "0.1" or "0.2" rounds to zero and is dropped, so a player who took the
 * field is reported as having never played that position, and because bench time
 * is derived as (game innings - field innings) they are also reported as benched
 * for innings they actually played.
 *
 * Conversion happens on READ, not at import. Existing gcFieldTime documents in
 * Firestore already hold GC's own notation, so converting here fixes historical
 * data with no migration and no writes.
 */

/**
 * Convert GC's outs notation to true innings.
 *
 *   "5.0" -> 5.000    "5.1" -> 5.333    "5.2" -> 5.667
 *
 * A fractional digit above 2 is not valid in this notation. If one ever appears
 * (a GC format change, or a hand-edited file) fall back to reading the value as a
 * plain decimal rather than silently inventing innings.
 */
export function gcInningsToDecimal(raw: unknown): number {
  if (raw === null || raw === undefined || raw === '') return 0;
  const s = String(raw).trim();
  if (!s || s === '-') return 0;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return 0;

  const dot = s.indexOf('.');
  if (dot === -1) return n;

  const frac = s.slice(dot + 1);
  // Only single-digit thirds notation is GC's format. Anything else is not it.
  if (frac.length !== 1) return n;
  const outs = Number(frac);
  if (!Number.isInteger(outs) || outs > 2) return n;

  const whole = Math.trunc(Math.abs(n));
  return whole + outs / 3;
}

/** Display helper: whole innings for narrow grid cells. Rounds ONCE, at the end
 *  of accumulation, so per-game rounding error can never compound. */
export function formatInnings(value: number): number {
  return Math.round(value);
}

/**
 * Display helper: real baseball innings notation, for the printable per-player
 * report where the number gets read by a parent.
 *
 *   11.333… -> "11.1"   (eleven innings and one out)
 *    7.666… -> "7.2"    (seven innings and two outs)
 *    3.0    -> "3.0"
 *
 * Innings are THIRDS, never decimals. The only valid fractional digits are 0, 1
 * and 2; a third out rolls over to the next whole inning. This replaces an
 * earlier tenths-rounding version that could print "4.7" — a quantity that does
 * not exist in baseball and reads as nonsense to exactly the audience this
 * report is written for.
 *
 * Rounds to the nearest OUT once, at the end of accumulation, so per-game
 * remainders can never compound into a phantom inning.
 */
export function formatInningsOuts(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0.0';
  const outs = Math.round(value * 3);
  return `${Math.floor(outs / 3)}.${outs % 3}`;
}

/**
 * How many innings a game's LIVE inningLogs actually cover.
 *
 * inningLogs are written one inning at a time, as the coach taps End Inning.
 * A coach who runs the live engine for three innings and then puts the phone
 * down leaves a genuine but PARTIAL record behind. Counting the keys is the
 * honest measure of that coverage.
 */
export function liveLoggedInnings(game: any): number {
  const logs = game?.inningLogs;
  if (!logs || typeof logs !== 'object') return 0;
  return Object.keys(logs).length;
}

/**
 * How many innings a game's GC import covers, measured as the MOST innings any
 * single player was on the field. One player can never exceed the length of the
 * game, and in a normal defensive game at least one player goes wire to wire, so
 * this is the tightest safe lower bound on game length available from the field
 * table alone. Used to size a game for bench math, and to tell whether a game
 * has an import at all (see fieldTimeRecordFor for which record wins).
 */
export function gcFieldTimeInnings(game: any): number {
  const ft = game?.gcFieldTime;
  if (!ft || typeof ft !== 'object') return 0;
  let max = 0;
  for (const positions of Object.values(ft) as any[]) {
    if (!positions || typeof positions !== 'object') continue;
    let total = 0;
    for (const v of Object.values(positions)) total += gcInningsToDecimal(v);
    if (total > max) max = total;
  }
  return max;
}

// ── Which record describes a game's field time ──────────────────────────────

/**
 * Team setting "Field positions come from" (Settings → Field Time).
 *   'gamechanger' (default): a GC import's field time wins over Dugout's live log.
 *   'dugout': a Dugout live log covering the whole game wins over GC. For
 *             coaches whose GC scorekeeper doesn't keep up with defensive
 *             changes (support log G9).
 * Pitching stats never depend on this; only who-played-where and bench time.
 */
export type FieldTimeSource = 'gamechanger' | 'dugout';

export function fieldTimeSourceOf(team: any): FieldTimeSource {
  return team?.settings?.fieldTimeSource === 'dugout' ? 'dugout' : 'gamechanger';
}

/**
 * How long a game was, in innings, for field time: the one answer every screen
 * uses. The app never stores a game's length, so this is measured.
 *
 *   With a GameChanger import: the longest of the import's measures. The stored
 *   inferredInnings comes from pitching and is a FLOOR (a staff pulled early
 *   lands short, and every fielder then looks like he never sat); the longest
 *   single fielder's day is firmer, since nobody outlasts the game.
 *   Without one: the team's Default Innings, else 6.
 *
 * Can be fractional (a walk-off ends the last inning early). Bench math wants
 * that; "did the log cover the whole game" rounds it up (fieldTimeRecordFor).
 */
export function gameLengthInnings(game: any, defaultInnings?: number): number {
  const measured = Math.max(gcFieldTimeInnings(game), game?.inferredInnings || 0, game?.innings || 0);
  return measured > 0 ? measured : (defaultInnings || 6);
}

/**
 * The record a game's field time comes from, under the team's setting. The one
 * rule shared by the Stats tab, the field-time report, imports' bench counts,
 * the Settings rebuild and the Game Positions editor, so they can never
 * disagree. Game length comes from gameLengthInnings, never from the caller.
 *
 *   'gc'      GameChanger's per-position totals
 *   'live'    Dugout's inning-by-inning log
 *   'partial' only a live log covering less than half the game: an abandoned
 *             attempt that describes nothing (and absences in it prove nothing)
 *   'none'    no field data at all
 *
 * Two different bars for a live log:
 *   - Against a GC import (Dugout setting only), it must cover the WHOLE game.
 *     A log that stops at inning 3 of 6 would otherwise replace GC's complete
 *     record and erase innings 4-6 from every kid's field time.
 *   - With no import, half the game is enough to be a real record, as before.
 */
export function fieldTimeRecordFor(
  game: any,
  source: FieldTimeSource,
  defaultInnings?: number,
): 'gc' | 'live' | 'partial' | 'none' {
  const live = liveLoggedInnings(game);
  const hasGc = gcFieldTimeInnings(game) > 0;
  const length = gameLengthInnings(game, defaultInnings);
  if (hasGc) {
    // GC innings are summed thirds, so a 6-inning game can measure 6.0000001.
    const wholeGame = live >= Math.ceil(length - 0.01);
    return source === 'dugout' && wholeGame ? 'live' : 'gc';
  }
  if (live > 0 && live >= length / 2) return 'live';
  return live > 0 ? 'partial' : 'none';
}
